import { useState } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import { toast } from 'react-toastify'

import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { ConfirmModal } from 'components/ConfirmModal'
import { Input } from 'components/Input'
import { oaCreationStrings } from 'constants/strings'
import {
  useCreateOaCreationJob,
  useCurrentUser,
} from 'hooks'
import useUploadFile from 'hooks/useUploadFile'
import type { TOaCreationJob } from 'types'

import {
  ICON_ACCEPT_ATTRIBUTE,
  ICON_MAX_BYTES,
  hasRecommendedIconDimensions,
  isAcceptedIconType,
  loadImageDimensions,
} from './validateProfileIcon'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type CreateFormProps = {
  onCreated?: (job: TOaCreationJob) => void
}

export function CreateForm({ onCreated }: CreateFormProps) {
  const strings = oaCreationStrings
  const { data: currentUser } = useCurrentUser()
  const { uploadToS3, loading: isUploading } = useUploadFile()

  const [businessAccount, setBusinessAccount] = useState('')
  const [accountName, setAccountName] = useState('')
  const [quantityInput, setQuantityInput] = useState('1')
  const [email, setEmail] = useState('')
  const [iconFile, setIconFile] = useState<File | null>(null)
  const [iconPreviewUrl, setIconPreviewUrl] = useState<string | null>(null)
  const [validationError, setValidationError] = useState<string | undefined>()
  const [iconDimensionWarning, setIconDimensionWarning] = useState<
    string | undefined
  >()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)

  const { create, isSubmitting, error: apiError } = useCreateOaCreationJob({
    onSuccess: (job) => {
      toast.success(strings.messages.createSuccess)
      resetForm()
      onCreated?.(job)
    },
  })

  const isBusy = isSubmitting || isUploading
  const error = validationError || apiError

  const resetForm = () => {
    setBusinessAccount('')
    setAccountName('')
    setQuantityInput('1')
    setEmail('')
    setIconFile(null)
    setIconDimensionWarning(undefined)
    setIconPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
  }

  const handleIconChange = async (event: ChangeEvent<HTMLInputElement>) => {
    setValidationError(undefined)
    setIconDimensionWarning(undefined)
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    if (!isAcceptedIconType(file)) {
      setValidationError(strings.errors.iconTypeInvalid)
      return
    }
    if (file.size > ICON_MAX_BYTES) {
      setValidationError(strings.errors.iconTooLarge)
      return
    }

    try {
      const { width, height } = await loadImageDimensions(file)
      if (!hasRecommendedIconDimensions(width, height)) {
        setIconDimensionWarning(
          strings.form.fields.icon.dimensionWarning
            .replace('$width', String(width))
            .replace('$height', String(height)),
        )
      }
    } catch {
      setValidationError(strings.errors.iconInvalidImage)
      return
    }

    setIconFile(file)
    setIconPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(file)
    })
  }

  const handleRemoveIcon = () => {
    setIconFile(null)
    setIconDimensionWarning(undefined)
    setIconPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
  }

  const validate = (): { quantity: number } | null => {
    const trimmedBusiness = businessAccount.trim()
    const trimmedAccountName = accountName.trim()
    const trimmedEmail = email.trim()
    if (!trimmedBusiness || !trimmedAccountName || !trimmedEmail) {
      setValidationError(strings.errors.required)
      return null
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setValidationError(strings.errors.emailInvalid)
      return null
    }
    const quantity = Number(quantityInput)
    if (!Number.isInteger(quantity) || quantity < 1) {
      setValidationError(strings.errors.quantityInvalid)
      return null
    }
    return { quantity }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isBusy) return
    setValidationError(undefined)
    if (!validate()) return
    setIsConfirmOpen(true)
  }

  const handleConfirm = async () => {
    const validated = validate()
    if (!validated) {
      setIsConfirmOpen(false)
      return
    }
    setIsConfirmOpen(false)

    let iconImageUrl: string | undefined
    if (iconFile) {
      try {
        const [uploaded] = await uploadToS3({ files: [iconFile] })
        iconImageUrl = uploaded?.uploadedUrl
      } catch (caught) {
        setValidationError(strings.errors.iconUploadFailed)
        toast.error(strings.errors.iconUploadFailed)
        // surface for visibility
        // eslint-disable-next-line no-console
        console.error('icon upload failed', caught)
        return
      }
    }

    create({
      businessAccount: businessAccount.trim(),
      accountName: accountName.trim(),
      quantity: validated.quantity,
      email: email.trim(),
      iconImageUrl,
    })
  }

  const confirmMessage = strings.confirm.messageTemplate
    .replace('$accountName', accountName.trim() || '-')
    .replace('$quantity', quantityInput || '1')

  return (
    <>
      <form
        className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
        onSubmit={handleSubmit}
      >
        <div className="grid gap-1">
          <h2 className="text-sm font-semibold text-ink">
            {strings.form.title}
          </h2>
          <p className="text-xs text-muted">{strings.form.description}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.businessAccount.label}
            <Input
              name="businessAccount"
              placeholder={strings.form.fields.businessAccount.placeholder}
              required
              disabled={isBusy}
              value={businessAccount}
              onChange={(event) => setBusinessAccount(event.target.value)}
            />
          </label>

          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.accountName.label}
            <Input
              name="accountName"
              placeholder={strings.form.fields.accountName.placeholder}
              required
              disabled={isBusy}
              value={accountName}
              onChange={(event) => setAccountName(event.target.value)}
            />
          </label>

          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.quantity.label}
            <Input
              name="quantity"
              type="number"
              min={1}
              placeholder={strings.form.fields.quantity.placeholder}
              required
              disabled={isBusy}
              value={quantityInput}
              onChange={(event) => setQuantityInput(event.target.value)}
            />
            <span className="text-[11px] font-normal text-muted">
              {strings.form.fields.quantity.hint}
            </span>
          </label>

          <label className="grid gap-2 text-xs font-semibold text-ink">
            {strings.form.fields.email.label}
            <Input
              name="email"
              type="email"
              placeholder={strings.form.fields.email.placeholder}
              required
              disabled={isBusy}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <span className="text-[11px] font-normal text-muted">
              {strings.form.fields.email.hint}
            </span>
          </label>
        </div>

        <div className="grid gap-2">
          <span className="text-xs font-semibold text-ink">
            {strings.form.fields.icon.label}
          </span>
          <div className="flex flex-wrap items-center gap-3">
            <label
              className={`inline-flex items-center justify-center rounded-2xl border border-border/80 bg-white px-4 py-2 text-xs font-semibold text-ink shadow-sm transition ${
                isBusy
                  ? 'cursor-not-allowed opacity-60'
                  : 'cursor-pointer hover:bg-surface'
              }`}
            >
              {iconFile
                ? strings.form.fields.icon.change
                : strings.form.fields.icon.select}
              <input
                type="file"
                accept={ICON_ACCEPT_ATTRIBUTE}
                className="hidden"
                disabled={isBusy}
                onChange={handleIconChange}
              />
            </label>
            {iconPreviewUrl && (
              <>
                <img
                  src={iconPreviewUrl}
                  alt="icon preview"
                  className="h-14 w-14 rounded-full border border-border/80 object-cover"
                />
                <button
                  type="button"
                  className="text-xs font-semibold text-danger underline-offset-2 hover:underline disabled:opacity-50"
                  disabled={isBusy}
                  onClick={handleRemoveIcon}
                >
                  {strings.form.fields.icon.remove}
                </button>
              </>
            )}
          </div>
          <span className="text-[11px] font-normal text-muted">
            {strings.form.fields.icon.accepted}
          </span>
          {iconDimensionWarning && (
            <span className="text-[11px] font-normal text-amber-700">
              {iconDimensionWarning}
            </span>
          )}
        </div>

        <div className="grid gap-2 rounded-2xl border border-border/60 bg-surface/40 p-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <span className="text-[11px] font-semibold text-muted">
              {strings.form.fields.creator.label}
            </span>
            <span className="text-xs font-semibold text-ink">
              {currentUser
                ? `${currentUser.name} (#${currentUser.id})`
                : '-'}
            </span>
            <span className="text-[11px] font-normal text-muted">
              {currentUser?.email ?? ''}
            </span>
          </div>
        </div>

        {error && <Alert>{error}</Alert>}

        <div className="flex justify-end">
          <Button size="md" type="submit" disabled={isBusy}>
            {isBusy ? strings.actions.submitting : strings.actions.create}
          </Button>
        </div>
      </form>

      <ConfirmModal
        open={isConfirmOpen}
        textTitle={strings.confirm.title}
        textMessage={confirmMessage}
        textCancel={strings.confirm.cancel}
        textConfirm={strings.confirm.confirm}
        isLoading={isBusy}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={() => {
          void handleConfirm()
        }}
      />
    </>
  )
}
